// Kalender-hjelparar (nynorsk). Alle datoar er ISO-strengar (YYYY-MM-DD) mot API-et.
import {
  addDays,
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  getISOWeek,
  parseISO,
  startOfMonth,
  startOfWeek,
} from "date-fns";

export const WEEKDAYS_NN = ["Mån", "Tys", "Ons", "Tor", "Fre", "Lau", "Sun"];
export const WEEKDAYS_MIN_NN = ["M", "T", "O", "T", "F", "L", "S"];
export const MONTHS_NN = [
  "Januar", "Februar", "Mars", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Desember",
];
export const MONTHS_SHORT_NN = ["Jan", "Feb", "Mar", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Des"];

export const VIEWS = ["month", "list", "year", "machines"];
export const KINDS = ["service", "annual"];
export const STATUSES = ["overdue", "upcoming", "ok"];
export const STATUS_RANK = { overdue: 0, upcoming: 1, ok: 2 };

export const toISO = (d) => format(d, "yyyy-MM-dd");
export const fromISO = (s) => parseISO(String(s).slice(0, 10));
export const monthKey = (d) => format(d, "yyyy-MM");
export const isoWeek = (d) => getISOWeek(d);

export const WEEKDAYS_LONG_NN = ["Måndag", "Tysdag", "Onsdag", "Torsdag", "Fredag", "Laurdag", "Sundag"];

export const monthTitle = (d) => `${MONTHS_NN[d.getMonth()]} ${d.getFullYear()}`;
export const dayTitle = (d) => `${WEEKDAYS_NN[(d.getDay() + 6) % 7]} ${d.getDate()}. ${MONTHS_NN[d.getMonth()].toLowerCase()}`;
export const dayTitleLong = (d) =>
  `${WEEKDAYS_LONG_NN[(d.getDay() + 6) % 7]} ${d.getDate()}. ${MONTHS_NN[d.getMonth()].toLowerCase()} ${d.getFullYear()}`;

export const worstStatus = (list) => {
  let best = null;
  for (const s of list) {
    if (!s || !(s in STATUS_RANK)) continue;
    if (best === null || STATUS_RANK[s] < STATUS_RANK[best]) best = s;
  }
  return best;
};

export const daysBetween = (fromISOStr, toISOStr) =>
  Math.round((fromISO(toISOStr) - fromISO(fromISOStr)) / 86400000);

/** 6 rader x 7 dagar (måndag først) som dekkjer heile månaden. */
export const monthGrid = (anchor) => {
  const start = startOfWeek(startOfMonth(anchor), { weekStartsOn: 1 });
  const end = endOfWeek(endOfMonth(anchor), { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start, end });
  const weeks = [];
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
  return weeks;
};

/** Indekser hendingar per dato: frist (due) og planlagt (planned). */
export const indexByDate = (events) => {
  const due = new Map();
  const planned = new Map();
  for (const e of events) {
    if (e.date) {
      if (!due.has(e.date)) due.set(e.date, []);
      due.get(e.date).push(e);
    }
    if (e.planned_date) {
      if (!planned.has(e.planned_date)) planned.set(e.planned_date, []);
      planned.get(e.planned_date).push(e);
    }
  }
  return { due, planned };
};

/** Effektiv arbeidsdato: planlagt dato dersom sett, elles fristen. */
export const effectiveDate = (e) => e.planned_date || e.date;

export const parseList = (value, all) => {
  if (!value) return [...all];
  const parts = String(value).split(",").map((s) => s.trim()).filter((s) => all.includes(s));
  return parts.length ? parts : [...all];
};

export const filterEvents = (events, { kinds, statuses, customer, q }) => {
  const needle = (q || "").trim().toLowerCase();
  return events.filter((e) => {
    if (kinds && !kinds.includes(e.kind)) return false;
    if (statuses && !statuses.includes(e.status)) return false;
    if (customer && e.customer_id !== customer) return false;
    if (needle) {
      const hay = [e.label, e.brand, e.type, e.category, e.customer_name, e.internal_number].filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    return true;
  });
};

export const filterMachines = (machines, { customer, q }) => {
  const needle = (q || "").trim().toLowerCase();
  return machines.filter((m) => {
    if (customer && m.customer_id !== customer) return false;
    if (needle) {
      const hay = [m.label, m.brand, m.type, m.category, m.customer_name, m.internal_number].filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    return true;
  });
};

/** Agenda-grupper: Forfalt / Denne veka / Neste 30 dagar / Seinare – basert på effektiv dato. */
export const groupAgenda = (events, todayISOStr) => {
  const today = fromISO(todayISOStr);
  const weekEnd = toISO(endOfWeek(today, { weekStartsOn: 1 }));
  const in30 = toISO(addDays(today, 30));
  const groups = [
    { key: "overdue", label: "Forfalt", hint: "Krev handling no", items: [] },
    { key: "week", label: "Denne veka", hint: `Til og med sundag ${format(endOfWeek(today, { weekStartsOn: 1 }), "d.M.")}`, items: [] },
    { key: "soon", label: "Neste 30 dagar", hint: "Planlegg kunderunder", items: [] },
    { key: "later", label: "Seinare", hint: "", items: [] },
  ];
  const sorted = [...events].sort((a, b) => effectiveDate(a).localeCompare(effectiveDate(b)) || a.label.localeCompare(b.label));
  for (const e of sorted) {
    const d = effectiveDate(e);
    if (e.status === "overdue" && !e.planned_date) groups[0].items.push(e);
    else if (d < todayISOStr) groups[0].items.push(e);
    else if (d <= weekEnd) groups[1].items.push(e);
    else if (d <= in30) groups[2].items.push(e);
    else groups[3].items.push(e);
  }
  return groups.filter((g) => g.items.length > 0);
};

/** Gruppér per kunde, sortert etter tidlegaste frist. */
export const groupByCustomer = (events) => {
  const map = new Map();
  for (const e of events) {
    const key = e.customer_id || "ukjend";
    if (!map.has(key)) {
      map.set(key, {
        customer_id: e.customer_id,
        customer_name: e.customer_name || "Ukjend kunde",
        customer_phone: e.customer_phone,
        customer_email: e.customer_email,
        customer_type: e.customer_type,
        items: [],
      });
    }
    map.get(key).items.push(e);
  }
  const groups = [...map.values()];
  for (const g of groups) {
    g.items.sort((a, b) => effectiveDate(a).localeCompare(effectiveDate(b)));
    g.first = effectiveDate(g.items[0]);
    g.worst = worstStatus(g.items.map((i) => i.status));
    g.machines = new Set(g.items.map((i) => i.machine_id)).size;
  }
  groups.sort((a, b) => (STATUS_RANK[a.worst] ?? 9) - (STATUS_RANK[b.worst] ?? 9) || a.first.localeCompare(b.first));
  return groups;
};

/** Kunderunde-forslag: kundar med >= 2 fristar (ulike maskiner eller typar) innan N dagar. */
export const customerRounds = (events, todayISOStr, windowDays = 45) => {
  const limit = toISO(addDays(fromISO(todayISOStr), windowDays));
  const soon = events.filter((e) => effectiveDate(e) <= limit);
  return groupByCustomer(soon).filter((g) => g.items.length >= 2);
};

/** Tel status per månad for eit år (til årsvisning). */
export const yearSummary = (events, year) => {
  const months = Array.from({ length: 12 }, () => ({ overdue: 0, upcoming: 0, ok: 0, planned: 0, total: 0 }));
  for (const e of events) {
    if (e.date && e.date.startsWith(String(year))) {
      const m = Number(e.date.slice(5, 7)) - 1;
      months[m][e.status] = (months[m][e.status] || 0) + 1;
      months[m].total += 1;
    }
    if (e.planned_date && e.planned_date.startsWith(String(year))) {
      const m = Number(e.planned_date.slice(5, 7)) - 1;
      months[m].planned += 1;
    }
  }
  return months;
};

export const shiftAnchor = (anchor, view, dir) => {
  if (view === "year" || view === "machines") return new Date(anchor.getFullYear() + dir, anchor.getMonth(), 1);
  return addMonths(anchor, dir);
};

export const KIND_LABEL = { service: "Service", annual: "Årskontroll" };
export const STATUS_LABEL_NN = { overdue: "Forfalt", upcoming: "Nærmar seg", ok: "OK", planned: "Planlagt" };
